import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { useUscgFocRequirementsFlag } from 'utils/featureFlags';
import {
  DEFAULT_ACTIVE_HURRICANES_SOURCE,
  DEFAULT_TROPICAL_CONDITIONS,
  useCreateOrgEventAutomationRule,
  useOrgEventAutomationRules,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import { NotificationType } from 'hooks/useNotification';

export const USCG_FOC_SE_DEFAULT_TROPICAL_RULE_NAME =
  'USCG FOC Southeast Tropical Impact';

const ensureLocks = new Map<string, Promise<unknown>>();

/**
 * Ensures the FOC group has a default tropical automation rule sourced from
 * Active Hurricanes / Tropical Cyclones (Global). Disabled by default so the
 * hardcoded Southeast threat notification stays primary.
 */
export function useEnsureUscgFocSeTropicalAutomationRule() {
  const focEnabled = useUscgFocRequirementsFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const { data: rules, isSuccess } = useOrgEventAutomationRules({
    enabled: focEnabled && !!groupGuid,
  });
  const createRule = useCreateOrgEventAutomationRule();
  const attempted = useRef(false);

  useEffect(() => {
    if (!focEnabled || !groupGuid || !isSuccess || attempted.current) return;

    const alreadyHasFocRule = (rules ?? []).some(
      (rule) => rule.name === USCG_FOC_SE_DEFAULT_TROPICAL_RULE_NAME
    );
    if (alreadyHasFocRule) {
      attempted.current = true;
      return;
    }

    const lockKey = `uscg-foc-se-tropical-rule:${groupGuid}`;
    if (ensureLocks.has(lockKey)) return;

    attempted.current = true;
    const promise = createRule
      .mutateAsync({
        group_guid: groupGuid,
        name: USCG_FOC_SE_DEFAULT_TROPICAL_RULE_NAME,
        enabled: false,
        logic: 'all',
        source: DEFAULT_ACTIVE_HURRICANES_SOURCE,
        conditions: DEFAULT_TROPICAL_CONDITIONS,
        event_template: {
          severity: 'High',
          type: 'Tropical Storm',
          region: 'Southeast District',
          thresholdDescription:
            'Storm category ≥ Tropical Storm (TS) and ≥ 1 USCG resource in forecast cone',
        },
        notify_on_create: false,
        checklist_on_create: false,
        checklist_config: { items: [] },
        notify_config: {
          notificationTypes: [NotificationType.IN_APP],
          userIds: userGuid ? [userGuid] : [],
          teamIds: [],
          priority: 'watch',
          titleTemplate: 'Threshold event: {eventName}',
          bodyTemplate:
            '{eventName}: {stormName} impacts {resourceCount} USCG assets.',
        },
        created_by_user_guid: userGuid ?? null,
      })
      .catch((err) => {
        console.warn(
          '[USCG FOC] failed to ensure Southeast tropical automation rule',
          err
        );
        attempted.current = false;
      })
      .finally(() => {
        ensureLocks.delete(lockKey);
      });

    ensureLocks.set(lockKey, promise);
  }, [focEnabled, groupGuid, userGuid, isSuccess, rules, createRule]);
}
